import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface SocialLink {
  platform: string;
  url: string;
}

interface Skill {
  icon: string;
  title: string;
  description: string;
  techStack: string[];
}

interface TimelineItem {
  date: string;
  title: string;
  subtitle: string;
  description: string;
}

interface ProjectItem {
  image: string;
  title: string;
  description: string;
  link: string;
}

interface Education {
  institution: string;
  degree: string;
  field: string;
  year: string;
  details: string;
}

interface AIWork {
  title: string;
  status: string;
  description: string;
  technologies: string[];
}

interface Certification {
  title: string;
  issuer: string;
  date: string;
  credential: string;
}

interface Leadership {
  title: string;
  description: string;
  impact: string[];
}

interface ContentData {
  main: {
    name: string;
    title: string;
    image: string;
    phone: string;
    email: string;
    socialLinks: SocialLink[];
  };
  expertise: Skill[];
  timeline: TimelineItem[];
  projects: ProjectItem[];
  educationJourney: Education[];
  aiWork: AIWork[];
  leadership: Leadership[];
  certifications: Certification[];
  footer: {
    socialLinks: SocialLink[];
    message: string;
  };
}

const defaultContent: ContentData = {
  main: {
    name: 'Your Name',
    title: 'Your Title',
    image: '/static/media/profile.123456.jpg',
    phone: '+1 234 567 8900',
    email: 'your.email@example.com',
    socialLinks: [
      { platform: 'GitHub', url: 'https://github.com/yourusername' },
      { platform: 'LinkedIn', url: 'https://linkedin.com/in/yourprofile' },
      { platform: 'Email', url: 'mailto:your.email@example.com' },
    ],
  },
  expertise: [
    {
      icon: 'faLaptopCode',
      title: 'Software Developer',
      description: 'Experienced in building scalable and user-friendly applications using Dart (Flutter), JavaScript (React Native, React.js, Node.js), Python, and C/C++. Proficient in modern frameworks and tools like Tailwind CSS, SCSS, Vite, and Firebase to create robust and dynamic solutions.',
      techStack: ["React", "React Native", "TypeScript", "JavaScript", "Node.js", "C/C++", "HTML5", "CSS3", "SASS", "Tailwind", "Firebase", "Python", "SQL", "PostgreSQL", "Vite", "Dart(Flutter)"],
    },
    {
      icon: 'faCogs',
      title: 'DevOps',
      description: 'Experienced in leveraging Firebase for cloud functions, hosting, and optimization. Skilled in automation, CI/CD, and infrastructure management to streamline workflows and improve efficiency.',
      techStack: ["Git", "GitHub Actions", "Vite", "TensorFlow", "Firebase", "LMS", "IT Support", "Linux"],
    },
    {
      icon: 'faServer',
      title: 'IT Specialist',
      description: 'Skilled in providing IT support and managing Learning Management Systems. Proficient in Microsoft Office Suite, ensuring efficiency in documentation, analysis, and presentations.',
      techStack: ["OpenAI", "Word", "Excel", "PowerPoint", "IT Support", "LMS"],
    },
    {
      icon: 'faChartBar',
      title: 'Data Analyst',
      description: 'Adept at analyzing and managing data with tools like Python and Excel. Expertise in data entry, visualization, and conducting research to deliver actionable insights.',
      techStack: ["Excel(Data Analysis)", "Python(Data Processing)", "Data Entry", "Data Management", "Research"],
    },
    {
      icon: 'faTools',
      title: 'Additional Skills',
      description: 'Creative and versatile with advanced expertise in graphic design, video editing using Camtasia, and camera/camcorder handling. Strong in research and data management for comprehensive project execution.',
      techStack: ["Graphic Design", "Camera & Camcorder Handling", "Video Editing", "Research"],
    },
  ],
  timeline: [
    {
      date: '2023 - Present',
      title: 'Founder and Contributor',
      subtitle: 'BlueSky Research and Information Service',
      description: 'Spearheaded IT services and data analysis projects, driving innovation and accuracy in data processing.',
    },
    {
      date: '2024 - Present',
      title: 'IT & Tech Specialist',
      subtitle: 'KNUST E-Learning, National Service',
      description: 'Provided IT support and contributed to the management of learning systems to enhance the educational experience.',
    },
    {
      date: '2022 - Present',
      title: 'Tutoring',
      subtitle: 'KNUST & Online',
      description: 'Delivered personalized lessons to students, enhancing their understanding of various courses, programming and IT concepts.',
    },
    {
      date: '2022 - 2024',
      title: 'Project Assistant',
      subtitle: 'KNUST',
      description: 'Assisted in developing three projects showcased on GitHub, demonstrating strong technical skills and teamwork.',
    },
    {
      date: '2020 - Present',
      title: 'Freelance Front-End Developer',
      subtitle: 'Online',
      description: 'Delivered tailored web solutions using modern technologies, ensuring client satisfaction and quality.',
    },
    {
      date: '2022 - 2024',
      title: 'IT Technician',
      subtitle: 'Part-Time',
      description: 'Provided technical support and resolved hardware/software issues, improving system reliability.',
    },
  ],
  projects: [
    {
      image: '/static/media/screenshot.png',
      title: 'My Portfolio',
      description: 'A personal portfolio website showcasing my skills, expertise, and projects. Built with React.js and modern tools, it reflects a clean, responsive, and professional design.',
      link: 'https://github.com/LosharHammond/Portfolio',
    },
    {
      image: '/static/media/medi.png',
      title: 'MediCare Mobile App',
      description: 'A comprehensive healthcare app designed to predict diseases, manage medical reports, and connect users with practitioners. Features include a feedback platform, real-time chat, and Firebase integration for secure data storage.',
      link: 'https://github.com/LosharHammond/Medicare-Connect-App',
    },
    {
      image: '/static/media/novellearn.png',
      title: 'NovelLearn: We entertain to learn',
      description: 'A React.js-based educational app that combines entertainment and learning by offering books and educational resources in a novel-like online format.',
      link: 'https://github.com/LosharHammond/NovelLearn',
    },
    {
      image: '/static/media/Screenshot0.png',
      title: 'Inventory Manager',
      description: 'A web app for tracking consumable and non-consumable items in organizations. Features include an intuitive interface for managing inventory, persistence with local storage, and dynamic item editing.',
      link: 'https://github.com/LosharHammond/Inventory-Manager',
    },
    {
      image: '/static/media/Screenshot5.png',
      title: 'Bhromaon: Front-End Design',
      description: 'A travel-focused app that helps users explore destinations, plan trips, and share their experiences. Includes real-time recommendations and user-generated content.',
      link: 'https://github.com/LosharHammond/Artboard',
    },
    {
      image: '/static/media/Screenshot7.png',
      title: 'Genesis:Front-End',
      description: 'A creative project management tool aimed at organizing and streamlining workflows for teams and individuals. Built with React.js and Firebase for seamless collaboration.',
      link: 'https://github.com/LosharHammond/Genesis-Webpage-Front-End-',
    },
    {
      image: '/static/media/Screenshot8.png',
      title: 'Productly:Front-End',
      description: 'A productivity app designed to manage tasks, set reminders, and boost efficiency. Built with a user-centric design for individuals and teams.',
      link: 'https://github.com/LosharHammond/Productly-website-frond-end',
    },
    {
      image: '/static/media/Screenshot5.png',
      title: 'My Page',
      description: 'A simple and dynamic personal web page featuring essential information, updates, and links to my work, designed to be engaging and easy to navigate.',
      link: 'https://github.com/LosharHammond/Profile',
    },
    {
      image: '/static/media/Screenshot3.png',
      title: 'LetTour',
      description: '"LetTour" is a tourism web app showcasing travel destinations, activities, and experiences. It allows users to discover and explore new places, read reviews, and plan their trips, making it a valuable tool for travelers. This project demonstrates your skills in building intuitive and functional web applications.',
      link: 'https://github.com/LosharHammond/LetTour',
    },
  ],
  educationJourney: [
    {
      institution: 'University Name',
      degree: 'Bachelor of Science',
      field: 'Computer Science',
      year: '2023',
      details: 'Graduated with honors.',
    },
  ],
  aiWork: [
    {
      title: 'AI Project',
      status: 'Completed',
      description: 'An AI project description.',
      technologies: ['Python', 'TensorFlow'],
    },
  ],
  leadership: [
    {
      title: 'Team Lead',
      description: 'Leading a development team.',
      impact: ['Improved productivity', 'Enhanced collaboration'],
    },
  ],
  certifications: [
    {
      title: 'Certification Name',
      issuer: 'Issuing Organization',
      date: '2023',
      credential: 'credential-link',
    },
  ],
  footer: {
    socialLinks: [
      { platform: 'GitHub', url: 'https://github.com/yourusername' },
    ],
    message: 'Portfolio built with React',
  },
};

interface ContentContextType {
  content: ContentData;
  updateContent: (newContent: ContentData) => void;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<ContentData>(defaultContent);

  useEffect(() => {
    const stored = localStorage.getItem('portfolioContent');
    if (stored) {
      setContent(JSON.parse(stored));
    }
  }, []);

  const updateContent = (newContent: ContentData) => {
    setContent(newContent);
    localStorage.setItem('portfolioContent', JSON.stringify(newContent));
  };

  return (
    <ContentContext.Provider value={{ content, updateContent }}>
      {children}
    </ContentContext.Provider>
  );
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};